import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users } from 'lucide-react';
import { VideoFeatures, Audience } from '@/store/videoFeatures';
import { ConfigurationTab } from './tabs/ConfigurationTab';
import { StyleTab } from './tabs/StyleTab';
import { t } from '@/utils/i18n';
import { HeroCard } from '@/components/ui/HeroCard';

interface AudienceConfigProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
}

export const AudienceConfig: React.FC<AudienceConfigProps> = ({
  features,
  setFeature,
}) => {
  useLanguage();
  const audience: Audience = features.audience || {
    enabled: false,
    viewersCount: 150,
    message: "Ao vivo — {count} pessoas assistindo agora",
    style: {
      textColor: '#ffffff',
      backgroundColor: '#000000',
      useTransparency: true,
      opacity: 70
    }
  };

  const handleAudienceToggle = (enabled: boolean) => {
    setFeature('audience', {
      ...audience,
      enabled
    });
  };

  const updateAudience = (key: string, value: any) => {
    setFeature('audience', {
      ...audience,
      [key]: value
    });
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Switch Mestre */}
      <div className="px-6 py-4">
        <HeroCard
          icon={<Users className="w-full h-full" strokeWidth={2} />}
          title={t('audience.enable')}
          description={t('audience.enable.helper')}
          isActive={audience.enabled}
          onToggle={handleAudienceToggle}
        />
      </div>

      {/* Tabs and Content */}
      {audience.enabled && (
        <div className="flex-1 flex flex-col px-6 pb-6 overflow-y-auto premium-scroll">
          <Tabs defaultValue="config">
            <TabsList className="grid w-full grid-cols-2 h-11 bg-[#F1F5F9] p-1 rounded-xl">
              <TabsTrigger
                value="config"
                className="h-9 rounded-lg text-[13px] font-medium data-[state=active]:bg-[#1D4FFF] data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm text-[#6B7280] transition-all duration-200"
              >
                {t('audience.tab.config')}
              </TabsTrigger>
              <TabsTrigger
                value="style"
                className="h-9 rounded-lg text-[13px] font-medium data-[state=active]:bg-[#1D4FFF] data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm text-[#6B7280] transition-all duration-200"
              >
                {t('audience.tab.style')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="config" className="m-0 mt-4">
              <div className="space-y-3 pb-6">
                <ConfigurationTab
                  audience={audience}
                  updateAudience={updateAudience}
                  isDisabled={!audience.enabled}
                />
              </div>
            </TabsContent>

            <TabsContent value="style" className="m-0 mt-4">
              <div className="space-y-3 pb-6">
                <StyleTab
                  audience={audience}
                  updateAudience={updateAudience}
                  isDisabled={!audience.enabled}
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
};
